import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';

import { getSmsDashboardScheduledMessagesKey } from './useScheduledMessages';
import { showResult } from '../utils';

import type { IScheduledMessagesListRes } from 'infrastructure/interfaces';

interface Props {
  onSuccess: () => void;
}
interface FnProps {
  conversationId: number;
  message: string;
  from: string;
  patientPhone: string;
  scheduledTime: string | null;
}

const sendScheduledMessageFn = async ({
  conversationId,
  message,
  from,
  patientPhone,
  scheduledTime,
}: FnProps) => {
  const API_SCHEDULED_MESSAGES = `core-2/conversations/scheduled-messages`;

  const { data, error } = await dataService.createOne(API_SCHEDULED_MESSAGES, {
    patientPhone,
    message,
    from,
    conversationId,
    scheduledTime,
  });

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useSendScheduledMessage = (_: unknown, { onSuccess }: Props) => {
  const queryClient = useQueryClient();

  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: sendScheduledMessageFn,
    onSuccess(data, variables) {
      const scheduledMessagesKey = getSmsDashboardScheduledMessagesKey({
        conversationId: variables.conversationId,
      });
      onSuccess();

      queryClient.setQueryData(
        scheduledMessagesKey,
        (oldMessages: IScheduledMessagesListRes | undefined) => {
          if (!oldMessages) return;
          return {
            ...oldMessages,
            items: [...oldMessages.items, data],
          };
        },
      );
    },
  });

  return {
    sendScheduledMessage: mutate,
    sendScheduledMessageLoading: isLoading,
    ...other,
  };
};
